import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Send, ShieldCheck } from "lucide-react";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["General", "Sponsorship", "Exhibitor", "Speaker"];

const emptyForm = {
  name: "",
  company: "",
  job_title: "",
  email: "",
  phone: "",
  message: "",
  enquiry_type: "General",
  captcha_answer: "",
};

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [captcha, setCaptcha] = useState({ id: "", question: "" });
  const [loading, setLoading] = useState(false);

  const fetchCaptcha = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/captcha`);
      setCaptcha(res.data);
      setForm((f) => ({ ...f, captcha_answer: "" }));
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error("captcha fetch failed", e);
      }
    }
  }, []);

  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Basic client-side check
    if (!form.name || !form.company || !form.job_title || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!form.captcha_answer) {
      toast.error("Please answer the captcha.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        captcha_question_id: captcha.id,
      };
      const res = await axios.post(`${API}/enquiries`, payload);
      toast.success(res.data.message || "Enquiry submitted successfully.");
      setForm(emptyForm);
      fetchCaptcha();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(String(detail));
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-11 rounded-md bg-[#0B1120] border border-cyan-900/60 px-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30 transition-colors";

  return (
    <section id="contact" data-testid={CONTACT.section} className="relative py-24 lg:py-32 border-t border-cyan-900/30 bg-[#050d1a]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="mono text-[11px] uppercase tracking-[0.24em] text-cyan-300">
            08 · Contact
          </div>
          <h2 className="font-display mt-4 text-3xl lg:text-4xl font-bold text-white">
            Get in touch.
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed">
            For general, sponsorship or exhibitor enquiries, please write to{" "}
            <a className="text-cyan-300" href="mailto:info@convex.events">
              info@convex.events
            </a>
            . For speaker enquiries, contact{" "}
            <a className="text-cyan-300" href="mailto:speaker@convex.events">
              speaker@convex.events
            </a>
            . You may also use the form — every submission is delivered to our
            team.
          </p>

          <div className="mt-10 space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 mt-1 text-cyan-300" />
              <p className="text-slate-300">
                Secure delivery — protected by a lightweight anti-bot check.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 mt-1 text-cyan-300" />
              <p className="text-slate-300">
                Your details are used only to respond to your enquiry.
              </p>
            </div>
          </div>
        </div>

        <form
          data-testid={CONTACT.form}
          onSubmit={submit}
          className="lg:col-span-7 rounded-lg border border-cyan-900/40 bg-[#0B1120] p-6 lg:p-8 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Name*</span>
              <input data-testid={CONTACT.name} className={inputCls + " mt-1"} value={form.name} onChange={update("name")} placeholder="Full name" required />
            </label>
            <label className="block">
              <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Company Name*</span>
              <input data-testid={CONTACT.company} className={inputCls + " mt-1"} value={form.company} onChange={update("company")} placeholder="Company / Organisation" required />
            </label>
            <label className="block">
              <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Job Title*</span>
              <input data-testid={CONTACT.jobTitle} className={inputCls + " mt-1"} value={form.job_title} onChange={update("job_title")} placeholder="e.g. CISO" required />
            </label>
            <label className="block">
              <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Email*</span>
              <input data-testid={CONTACT.email} type="email" className={inputCls + " mt-1"} value={form.email} onChange={update("email")} placeholder="you@company.com" required />
            </label>
            <label className="block">
              <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Phone Number*</span>
              <input data-testid={CONTACT.phone} className={inputCls + " mt-1"} value={form.phone} onChange={update("phone")} placeholder="+63 ..." required />
            </label>
            <label className="block">
              <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Enquiry Type</span>
              <select
                data-testid={CONTACT.type}
                className={inputCls + " mt-1 appearance-none pr-8"}
                value={form.enquiry_type}
                onChange={update("enquiry_type")}
              >
                {TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#0B1120] text-white">
                    {t}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Message*</span>
            <textarea
              data-testid={CONTACT.message}
              rows={5}
              className={inputCls + " h-auto py-3 mt-1"}
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us how we can help..."
              required
            />
          </label>

          {/* Captcha */}
          <div className="rounded-md border border-cyan-900/50 bg-[#050d1a] p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
                Verify you&apos;re human
              </div>
              <div className="mono text-cyan-200 text-sm truncate">
                {captcha.question || "Loading..."}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                data-testid={CONTACT.captcha}
                className="h-10 w-24 rounded-md bg-[#0B1120] border border-cyan-900/60 px-3 text-sm text-white focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30"
                value={form.captcha_answer}
                onChange={update("captcha_answer")}
                placeholder="Answer"
                inputMode="numeric"
                required
              />
              <button
                type="button"
                data-testid={CONTACT.captchaRefresh}
                onClick={fetchCaptcha}
                className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-cyan-500/40 text-cyan-200 hover:bg-cyan-400/10"
                aria-label="Refresh captcha"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          <button
            type="submit"
            data-testid={CONTACT.submit}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 disabled:opacity-60 disabled:cursor-not-allowed text-slate-950 font-semibold px-6 py-3 transition-colors"
          >
            {loading ? "Submitting..." : "Submit Enquiry"}
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
